import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { AIFieldContext } from './AIAssistantContexts';

interface InlineAIAssistProps {
  fieldContext: AIFieldContext;
  currentValue: string;
  onUpdate: (value: string) => void;
  placeholder?: string;
}

interface InlineAIAssistRef {
  handleQuickStart: () => void;
  isActive: () => boolean;
}

interface QuickSuggestion {
  text: string;
  type: 'template' | 'example' | 'guidance';
}

const InlineAIAssist = forwardRef<InlineAIAssistRef, InlineAIAssistProps>(({ 
  fieldContext, 
  currentValue, 
  onUpdate, 
  placeholder
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [userResponse, setUserResponse] = useState('');
  const [conversationStep, setConversationStep] = useState(0);
  const [quickSuggestions, setQuickSuggestions] = useState<QuickSuggestion[]>([]);
  const [conversationHistory, setConversationHistory] = useState<Array<{assistant: string, user: string}>>([]);
  const [generatedResponse, setGeneratedResponse] = useState<string>('');
  const [showAcceptance, setShowAcceptance] = useState(false);

  // Expose handleQuickStart and isActive to parent component
  useImperativeHandle(ref, () => ({
    handleQuickStart,
    isActive: () => isExpanded || showAcceptance
  }));

  // Quick suggestions based on field type
  useEffect(() => {
    if (fieldContext.fieldType === 'passport-card-explanation') {
      setQuickSuggestions([
        { text: "I checked my records and documents...", type: 'template' },
        { text: "I contacted family members to verify...", type: 'template' },
        { text: "After reviewing my passport history...", type: 'template' }
      ]);
    }
  }, [fieldContext.fieldType]);

  const handleQuickStart = async () => {
    setIsExpanded(true);
    setIsLoading(true);
    
    try {
      const response = await callAIAssistant('I need help explaining my passport card status', 'initial');
      if (response.success && response.guidance?.questions && response.guidance.questions.length > 0) {
        // Always use only the first question, even if multiple are returned
        setCurrentQuestion(response.guidance.questions[0]);
        setConversationStep(0); // Start at 0 for first question
      }
    } catch (error) {
      console.error('AI assist error:', error);
      setCurrentQuestion("What steps have you taken to verify your passport card status?");
      setConversationStep(0); // Start at 0 for first question
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponseSubmit = async () => {
    if (!userResponse.trim()) return;
    
    setIsLoading(true);
    
    try {
      // Add to conversation history
      const newHistory = [...conversationHistory, {
        assistant: currentQuestion,
        user: userResponse
      }];
      setConversationHistory(newHistory);
      
      // Continue interview and let AI decide when to finalize
      const response = await callAIAssistant(userResponse, 'interview');
        
      if (response.success && response.guidance) {
        if (response.guidance.type === 'ready_to_finalize' || response.guidance.type === 'finalized_response') {
          // Generate final response
          const finalResponse = await callAIAssistant(userResponse, 'finalize');
          if (finalResponse.success && finalResponse.guidance?.response) {
            setGeneratedResponse(finalResponse.guidance.response);
            setShowAcceptance(true);
          }
        } else if (response.guidance.questions && response.guidance.questions.length > 0) {
          // Always use only the first question, even if multiple are returned
          setCurrentQuestion(response.guidance.questions[0]);
          setConversationStep(prev => prev + 1);
          setUserResponse('');
        } else {
          // No more questions, finalize
          const finalResponse = await callAIAssistant(userResponse, 'finalize');
          if (finalResponse.success && finalResponse.guidance?.response) {
            setGeneratedResponse(finalResponse.guidance.response);
            setShowAcceptance(true);
          }
        }
      }
    } catch (error) {
      console.error('AI response error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setIsExpanded(false);
    setConversationStep(0);
    setUserResponse('');
    setCurrentQuestion('');
    setConversationHistory([]);
    setGeneratedResponse('');
    setShowAcceptance(false);
  };

  const handleAcceptResponse = () => {
    onUpdate(generatedResponse);
    handleReset();
  };

  const handleRejectResponse = () => {
    setShowAcceptance(false);
    setGeneratedResponse('');
    // Stay in the conversation to allow user to provide more info or restart
  };

  const handleRestartInterview = () => {
    setShowAcceptance(false);
    setGeneratedResponse('');
    setConversationStep(0);
    setUserResponse('');
    setCurrentQuestion('');
    setConversationHistory([]);
    handleQuickStart();
  };

  const callAIAssistant = async (userInput: string, interactionType: string) => {
    const endpoint = process.env.REACT_APP_AI_ASSIST_ENDPOINT || 'https://20n73zcs4g.execute-api.us-east-1.amazonaws.com/prod/assist';
    
    const payload = {
      field_context: fieldContext,
      user_input: userInput,
      interaction_type: interactionType,
      conversation_history: conversationHistory
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`AI Assist API error: ${response.status}`);
    }

    return await response.json();
  };

  const handleQuickSuggestionClick = (suggestion: QuickSuggestion) => {
    const enhanced = currentValue ? `${currentValue} ${suggestion.text}` : suggestion.text;
    onUpdate(enhanced);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleResponseSubmit();
    }
  };


  return (
    <div className="relative">
      {/* Quick suggestions when field is empty and not expanded */}
      {!isExpanded && !currentValue && quickSuggestions.length > 0 && (
        <div className="flex gap-1 mt-2">
          {quickSuggestions.slice(0, 2).map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleQuickSuggestionClick(suggestion)}
              className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
              title={`Quick start: ${suggestion.text}`}
            >
              {suggestion.text.slice(0, 20)}...
            </button>
          ))}
        </div>
      )}

      {/* AI Assistant Overlay during Interview */}
      {isExpanded && !showAcceptance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">AI Assistant</h3>
                    <p className="text-xs text-gray-500">Gathering information to help improve your response</p>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Current Question */}
              {currentQuestion && (
                <div className="mb-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900 font-medium mb-1">Question:</p>
                    <p className="text-sm text-blue-800">{currentQuestion}</p>
                  </div>
                </div>
              )}

              {/* Response Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Response:</label>
                  <textarea
                    value={userResponse}
                    onChange={(e) => setUserResponse(e.target.value)}
                    onKeyPress={handleKeyPress}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Type your response here..."
                    disabled={isLoading}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Press Enter to continue</span>
                  <button
                    onClick={handleResponseSubmit}
                    disabled={isLoading || !userResponse.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </div>
                    ) : 'Continue'}
                  </button>
                </div>

                {/* Simple progress indicator without steps */}
                <div className="pt-2">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-500">Gathering your information</span>
                    <span className="text-xs text-blue-600 font-medium">In progress</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full transition-all duration-500 animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Response Acceptance Dialog */}
      {showAcceptance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full mr-3">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Generated Response</h3>
                  <p className="text-sm text-gray-500">Review and accept or modify the AI-generated response</p>
                </div>
              </div>

              {/* Generated Response Preview */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">AI Generated Response:</label>
                <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{generatedResponse}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleRejectResponse}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Modify
                </button>
                <button
                  onClick={handleRestartInterview}
                  className="flex-1 px-4 py-2 text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Start Over
                </button>
                <button
                  onClick={handleAcceptResponse}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Accept & Use
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

InlineAIAssist.displayName = 'InlineAIAssist';

export default InlineAIAssist;